/**
 * Step 4, saved lists — reuse the recipients of an earlier send.
 *
 * Picking one runs the same create call as the other two routes, so a saved
 * list gets the same per-row report: a list that was valid last month can
 * still hold an address that has since bounced.
 *
 * The design draws the route pill but not this panel, so the chrome is the
 * template picker's, which is the closest existing "choose one of these" list.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Skeleton, Tooltip, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import EmptyState from "global/components/EmptyState/EmptyState";
import { ConfirmModal } from "global/components/AppModal";
import {
  calculateRelativeTime,
  handleErrorMessage,
  openSuccessNotification,
} from "helpers/methods";
import { templatePickerStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import { BulkRecipientList } from "../BulkSendTypesAndValidation";
import { recipientCountLabel } from "../BulkSendHelper";
import {
  deleteBulkRecipientList,
  fetchBulkRecipientLists,
} from "../BulkSendService";
import { CloseIcon } from "./BulkSendIcons";

const t = tokens.templatePicker;
const s = templatePickerStyles;

interface Props {
  isSubmitting: boolean;
  onUse: (listId: string, listName: string) => void;
  onUploadInstead: () => void;
}

const SavedListPicker: React.FC<Props> = ({
  isSubmitting,
  onUse,
  onUploadInstead,
}) => {
  const [lists, setLists] = useState<BulkRecipientList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasFailed, setHasFailed] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BulkRecipientList | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasFailed(false);
      const response = await fetchBulkRecipientLists(1);
      setLists(response);
    } catch (error: any) {
      setHasFailed(true);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      setIsDeleting(true);
      await deleteBulkRecipientList(pendingDelete.id);
      openSuccessNotification(`Deleted "${pendingDelete.name}".`);
      if (selectedId === pendingDelete.id) setSelectedId(null);
      setPendingDelete(null);
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={s.list}>
        {[0, 1, 2].map((key) => (
          <Box key={key} sx={s.card(false)}>
            <Box sx={s.cardRow}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  sx={[s.cardName, { width: "46%" }] as SxProps<Theme>}
                />
                <Skeleton
                  variant="text"
                  sx={[s.cardMeta, { width: "28%" }] as SxProps<Theme>}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (hasFailed) {
    return (
      <EmptyState
        size="medium"
        icon="contact"
        title="Couldn't load your saved lists"
        body="The request didn't come back. Try again in a moment."
        primaryAction={{ label: "Try again", onClick: load }}
        secondaryAction={{
          label: "Upload a CSV instead",
          onClick: onUploadInstead,
        }}
      />
    );
  }

  if (lists.length === 0) {
    return (
      <EmptyState
        size="medium"
        icon="contact"
        title="No saved lists yet"
        body="Save the recipients of a send and they'll be here to reuse next time."
        primaryAction={{
          label: "Upload a CSV instead",
          onClick: onUploadInstead,
        }}
      />
    );
  }

  return (
    <>
      <Box sx={s.list}>
        {lists.map((list) => {
          const isSelected = selectedId === list.id;
          return (
            <Box
              key={list.id}
              component="button"
              type="button"
              aria-pressed={isSelected}
              onClick={() => setSelectedId(list.id)}
              sx={s.card(isSelected)}
            >
              <Box component="span" sx={s.cardRow}>
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  <Box component="span" sx={s.cardName}>
                    {list.name}
                  </Box>
                  <Box component="span" sx={s.cardMeta}>
                    {typeof list.rowCount === "number"
                      ? recipientCountLabel(list.rowCount)
                      : "Saved list"}
                    {list.createdOn
                      ? ` · ${calculateRelativeTime(list.createdOn)}`
                      : ""}
                  </Box>
                </Box>
                <Tooltip title="Delete list">
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    aria-label={`Delete ${list.name}`}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      setPendingDelete(list);
                    }}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        setPendingDelete(list);
                      }
                    }}
                    sx={s.roleOrder}
                  >
                    <CloseIcon size={10} />
                  </Box>
                </Tooltip>
                <Box component="span" sx={s.radio(isSelected)} />
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: "16px" }}>
        <Button
          variant="contained"
          disabled={!selectedId || isSubmitting}
          onClick={() => {
            const chosen = lists.find((list) => list.id === selectedId);
            if (chosen) onUse(chosen.id, chosen.name);
          }}
          sx={{
            height: t.footerButtonHeight,
            borderRadius: t.footerButtonRadius,
          }}
        >
          {isSubmitting ? "Checking…" : "Use this list"}
        </Button>
      </Box>

      <ConfirmModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        tone="destructive"
        title={`Delete "${pendingDelete?.name}"?`}
        message="The list is removed for everyone in this workspace. Batches already sent from it are unaffected."
        confirmLabel="Delete list"
        cancelLabel="Keep it"
        loading={isDeleting}
        onConfirm={handleDelete}
      />

      <Typography
        component="p"
        sx={{
          pt: "12px",
          fontSize: t.metaFontSize,
          color: tokens.palette.muted,
        }}
      >
        Every row is checked again before anything is sent — an address that
        worked last time may not still.
      </Typography>
    </>
  );
};

export default SavedListPicker;
